import { useSyncExternalStore } from 'react';

/**
 * How much of a job's inventory has arrived so far.
 *
 * `GET /item` serves 50 items per page, and the review screen renders as soon
 * as the first page lands rather than waiting for the last one. That means the
 * list on screen is briefly incomplete, and anything that reads the whole
 * inventory -- the spreadsheet export above all -- has to be able to tell.
 */
export type ItemsLoadProgress = {
  loadedItems: number;
  totalItems: number;
  loadedPages: number;
  totalPages: number;
};

/** True while more pages of this job's inventory are still on the way. */
export const isLoadingMoreItems = (progress: ItemsLoadProgress | undefined): boolean =>
  !!progress && progress.loadedPages < progress.totalPages;

// A plain store rather than a react-query entry: this is progress *about* a
// query, not server state of its own, and keeping it out of the cache means it
// cannot be mistaken for data or invalidated alongside it.
const progressByJob = new Map<string, ItemsLoadProgress>();
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export const setItemsLoadProgress = (jobKey: string, progress: ItemsLoadProgress) => {
  progressByJob.set(jobKey, progress);
  emit();
};

export const clearItemsLoadProgress = (jobKey: string) => {
  if (progressByJob.delete(jobKey)) emit();
};

export const getItemsLoadProgress = (jobKey: string): ItemsLoadProgress | undefined => progressByJob.get(jobKey);

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Subscribe a component to how much of `jobKey`'s inventory has loaded. */
export const useItemsLoadProgress = (jobKey: string | undefined): ItemsLoadProgress | undefined =>
  useSyncExternalStore(subscribe, () => (jobKey ? progressByJob.get(jobKey) : undefined));
